import { useMemo, useCallback, useState } from 'react';
import { useTableContext } from '@antimatterhq/ui/components/table';
import {
  FieldList,
  Field,
  FieldSortOption,
} from '@antimatterhq/ui/components/fieldList';
import { TABLE_SELECT_COLUMN_ID } from '@antimatterhq/ui/constants/table';
import { compareColumnPositions } from '@antimatterhq/ui/utils/table/compareColumnPositions';

interface QueryTableFieldListProps {
  columnVisibility: Record<string, boolean>;
  columnOrders: Record<string, number>;
  pinnedColumns: Record<string, 'left' | 'right' | undefined>;
  toggleColumnVisibility: (columnId: string) => void;
  onColumnOrderChange: (id: string, oldIndex: number, newIndex: number) => void;
  setAllColumnOrders: (orderedColumnIds: string[]) => void;
  className?: string;
  'data-testid'?: string;
}

export function QueryTableFieldList({
  columnVisibility,
  columnOrders,
  pinnedColumns,
  toggleColumnVisibility,
  onColumnOrderChange,
  setAllColumnOrders,
  className,
  'data-testid': dataTestId = 'query-table-field-list',
}: QueryTableFieldListProps) {
  const { columnDefinitions } = useTableContext();
  const [fieldFilter, setFieldFilter] = useState('');
  const [fieldSort, setFieldSort] = useState<FieldSortOption | null>(null);

  // TODO: Implement this
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getItemCount = useCallback((_columnId: string) => {
    return 0;
  }, []);

  // Generate fields from ALL column definitions
  const fields = useMemo((): Field[] => {
    // Count of visible non-pinned columns
    const visibleNonPinnedCount = Object.keys(columnVisibility).filter(
      (id) => !pinnedColumns[id] && columnVisibility[id],
    ).length;

    // Filter and map columns to fields
    const unsortedFields = columnDefinitions
      .filter((col) => col.id !== 'edit' && col.id !== TABLE_SELECT_COLUMN_ID)
      .map((col) => {
        const visible = columnVisibility[col.id] !== false;
        const disabled =
          !pinnedColumns[col.id] && visible && visibleNonPinnedCount === 1;

        return {
          id: col.id,
          name: col.title as string,
          count: getItemCount(col.id),
          checked: visible,
          disabled,
        } as Field;
      });

    // Sort fields according to columnOrders and pinned status
    return unsortedFields.sort((a, b) =>
      compareColumnPositions(a, b, pinnedColumns, columnOrders),
    );
  }, [
    columnVisibility,
    columnDefinitions,
    pinnedColumns,
    getItemCount,
    columnOrders,
  ]);

  // Handle sort change by updating column orders
  const handleSortChange = useCallback(
    (sortOption: FieldSortOption | null) => {
      setFieldSort(sortOption);

      if (!sortOption) {
        // Reset to original order (TODO: implement reset)
        return;
      }

      // Get a copy of the fields to sort & Apply the selected sort
      const sortedFields = [...fields].sort((a, b) => {
        switch (sortOption) {
          case 'name-asc':
            return a.name.localeCompare(b.name);
          case 'name-desc':
            return b.name.localeCompare(a.name);
          case 'count-asc':
            return a.count - b.count;
          case 'count-desc':
            return b.count - a.count;
          default:
            return 0;
        }
      });

      // Use the efficient batch update
      const orderedIds = sortedFields.map((f) => f.id);
      setAllColumnOrders(orderedIds);
    },
    [fields, setAllColumnOrders],
  );

  // Filter fields for display (sorting is now handled by updating column orders)
  const displayedFields = useMemo(() => {
    if (!fieldFilter) {
      return fields;
    }

    return fields.filter((field) =>
      field.name.toLowerCase().includes(fieldFilter.toLowerCase()),
    );
  }, [fields, fieldFilter]);

  // Handle reordering
  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      const fromField = displayedFields[fromIndex];
      const toField = displayedFields[toIndex];

      if (!fromField || !toField) return;

      // Get ALL column IDs sorted properly
      const sortedColumnIds = Object.keys(columnOrders)
        .map((id) => ({ id }))
        .sort((a, b) =>
          compareColumnPositions(a, b, pinnedColumns, columnOrders),
        )
        .map((item) => item.id);

      const oldIndex = sortedColumnIds.indexOf(fromField.id);
      const newIndex = sortedColumnIds.indexOf(toField.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        onColumnOrderChange(fromField.id, oldIndex, newIndex);
      }
    },
    [displayedFields, columnOrders, pinnedColumns, onColumnOrderChange],
  );

  // Toggle all handler
  const handleToggleAll = useCallback(() => {
    const toggleableFields = displayedFields.filter((f) => !f.disabled);
    const allChecked =
      toggleableFields.length > 0 &&
      toggleableFields.every((field) => columnVisibility[field.id] !== false);

    toggleableFields.forEach((field) => {
      const currentVisibility = columnVisibility[field.id] !== false;
      if (currentVisibility !== !allChecked) {
        toggleColumnVisibility(field.id);
      }
    });
  }, [displayedFields, columnVisibility, toggleColumnVisibility]);

  return (
    <div className={className} data-testid={dataTestId}>
      <FieldList
        fields={displayedFields}
        onFieldToggle={toggleColumnVisibility}
        onToggleAll={handleToggleAll}
        onReorder={handleReorder}
        filterValue={fieldFilter}
        sortOrder={fieldSort}
        onFilterChange={setFieldFilter}
        onSortChange={handleSortChange}
        className="h-full overflow-y-auto"
        enableDragAndDrop
        showFilter
        showSort
        stickyHeader
      />
    </div>
  );
}
